/**
 * useScrollDepth Hook
 *
 * Tracks scroll depth milestones (25%, 50%, 75%, 100%) and reports to analytics.
 * Resets tracking when the component unmounts or path changes.
 */

import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { trackScrollDepth, resetScrollDepthTracking } from '@/services/analyticsService';

type ScrollDepthMilestone = 25 | 50 | 75 | 100;

interface UseScrollDepthOptions {
  /** Whether to enable scroll tracking (default: true) */
  enabled?: boolean;
  /** Throttle delay in ms (default: 100) */
  throttleMs?: number;
}

export const useScrollDepth = (options: UseScrollDepthOptions = {}): void => {
  const { enabled = true, throttleMs = 100 } = options;
  const location = useLocation();
  const lastPathRef = React.useRef(location.pathname);
  const milestonesReachedRef = React.useRef<Set<ScrollDepthMilestone>>(new Set());

  React.useEffect(() => {
    if (!enabled) return;

    // Reset when path changes
    if (lastPathRef.current !== location.pathname) {
      milestonesReachedRef.current.clear();
      resetScrollDepthTracking();
      lastPathRef.current = location.pathname;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const calculateScrollDepth = (): number => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // If content doesn't require scrolling, consider it 100%
      if (scrollHeight <= clientHeight) {
        return 100;
      }

      const scrollableHeight = scrollHeight - clientHeight;
      return Math.round((scrollTop / scrollableHeight) * 100);
    };

    const checkMilestones = (): void => {
      const depth = calculateScrollDepth();
      const milestones: ScrollDepthMilestone[] = [25, 50, 75, 100];

      for (const milestone of milestones) {
        if (depth >= milestone && !milestonesReachedRef.current.has(milestone)) {
          milestonesReachedRef.current.add(milestone);
          trackScrollDepth(milestone);
        }
      }
    };

    const handleScroll = (): void => {
      if (timeoutId) return;

      timeoutId = setTimeout(() => {
        checkMilestones();
        timeoutId = null;
      }, throttleMs);
    };

    // Check initial scroll position (for pages that load scrolled)
    checkMilestones();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [enabled, throttleMs, location.pathname]);

  // Reset on unmount
  React.useEffect(() => {
    return () => {
      resetScrollDepthTracking();
    };
  }, []);
};

export default useScrollDepth;
